<template>
  <div id="panel-right" class="panel-right" :style="{ width: panelWidth + 'px', minWidth: panelWidth + 'px' }">
    <!-- Resize handle (drag left edge to resize) -->
    <div class="right-resize-handle" :class="{ dragging: isResizing }" @mousedown.prevent="startResize"></div>
    <!-- Report Context Badge -->
    <div class="right-context" :style="{ '--ctx-color': currentDept.color }">
      <span class="right-context-icon">{{ currentDept.icon }}</span>
      <span class="right-context-label">{{ isOverview ? 'Overview' : currentReport.label }}</span>
      <span class="right-context-dept">{{ currentDept.label }}</span>
    </div>

    <!-- Toggle -->
    <div class="right-toggle">
      <button
        class="right-toggle-btn"
        :class="{ active: activePanel === 'summary' }"
        @click="activePanel = 'summary'"
      >🧠 AI Summary</button>
      <button
        class="right-toggle-btn"
        :class="{ active: activePanel === 'chat' }"
        @click="activePanel = 'chat'"
      >💭 Chat AI</button>
      <button
        v-if="canAccessPrompt"
        class="right-toggle-btn"
        :class="{ active: activePanel === 'prompt' }"
        @click="activePanel = 'prompt'"
      >📝 Prompt</button>
      <button
        v-if="canAccessUsers"
        class="right-toggle-btn"
        :class="{ active: activePanel === 'users' }"
        @click="activePanel = 'users'"
      >👥 Users</button>
      <button
        v-if="canAccessUsers"
        class="right-toggle-btn"
        :class="{ active: activePanel === 'mining' }"
        @click="activePanel = 'mining'"
      >🔬 Mining</button>
    </div>

    <!-- Summary Panel -->
    <div v-show="activePanel === 'summary'" class="right-content" ref="summaryScrollEl" @scroll="onSummaryScroll" @click="showSummaryExport = false">

      <!-- Export bar for AI Summary -->
      <div class="summary-export-bar">
        <span class="summary-export-label">🧠 AI Summary</span>
        <button class="export-ctx-btn" @click="handleSummaryExport('clipboard')">
          📋 Copy to Clipboard
        </button>
      </div>

      <!-- Summary Box -->
      <div class="summary-box" ref="summaryBoxEl">
        <!-- Loading state -->
        <div v-if="wfLoading" style="font-size: 12px; color: var(--text-ghost); padding: 8px 0">
          Memuat data workflow...
        </div>

        <!-- Parsed workflow content with tagged sections -->
        <template v-else-if="hasWorkflowMd && hasParsedSections">

          <!-- Executive Summary section -->
          <div v-if="parsedMdContent.executiveSummary" class="ai-tag-section ai-tag-executive">
            <div class="ai-tag-header">
              <span class="ai-tag-icon">🧠</span>
              <span class="ai-tag-title">Executive Summary</span>
            </div>
            <div class="ai-tag-body">
              <MarkdownRenderer :content="parsedMdContent.executiveSummary" />
            </div>
          </div>

          <!-- Todo List — accordion cards -->
          <div v-if="parsedMdContent.todoPoints.length" class="ai-tag-section ai-tag-todo">
            <div class="ai-tag-header">
              <span class="ai-tag-icon">📋</span>
              <span class="ai-tag-title">Todo List</span>
              <span class="ai-tag-count">{{ parsedMdContent.todoPoints.length }}</span>
            </div>
            <div class="ai-tag-body ai-accordion-points ai-accordion-todo">
              <details
                v-for="(point, idx) in parsedMdContent.todoPoints"
                :key="'todo-' + idx"
                class="ai-accordion"
                :open="idx === 0"
              >
                <summary class="ai-accordion-summary">
                  <span class="ai-accordion-idx">{{ idx + 1 }}</span>
                  <span class="ai-accordion-title">{{ getPointTitle(point) }}</span>
                  <span class="ai-accordion-chevron"></span>
                </summary>
                <div class="ai-accordion-body">
                  <MarkdownRenderer :content="getPointBody(point)" />
                </div>
              </details>
            </div>
          </div>

          <!-- Todo List — legacy markdown list -->
          <div v-else-if="parsedMdContent.todoList" class="ai-tag-section ai-tag-todo">
            <div class="ai-tag-header">
              <span class="ai-tag-icon">📋</span>
              <span class="ai-tag-title">Todo List</span>
            </div>
            <div class="ai-tag-body">
              <MarkdownRenderer :content="parsedMdContent.todoList" />
            </div>
          </div>

          <!-- Detail section — accordion cards -->
          <div v-if="parsedMdContent.detailPoints.length" class="ai-tag-section ai-tag-detail">
            <div class="ai-tag-header">
              <span class="ai-tag-icon">📄</span>
              <span class="ai-tag-title">Detail</span>
              <span class="ai-tag-count">{{ parsedMdContent.detailPoints.length }}</span>
            </div>
            <div class="ai-tag-body ai-accordion-points ai-accordion-detail">
              <details
                v-for="(point, idx) in parsedMdContent.detailPoints"
                :key="'detail-' + idx"
                class="ai-accordion"
                :open="idx === 0"
              >
                <summary class="ai-accordion-summary">
                  <span class="ai-accordion-idx">{{ idx + 1 }}</span>
                  <span class="ai-accordion-title">{{ getPointTitle(point) }}</span>
                  <span class="ai-accordion-chevron"></span>
                </summary>
                <div class="ai-accordion-body">
                  <MarkdownRenderer :content="getPointBody(point)" />
                </div>
              </details>
            </div>
          </div>

          <!-- Detail section — raw fallback -->
          <div v-else-if="parsedMdContent.detail || parsedMdContent.remaining" class="ai-tag-section ai-tag-detail">
            <div class="ai-tag-header">
              <span class="ai-tag-icon">📄</span>
              <span class="ai-tag-title">Detail</span>
            </div>
            <div class="ai-tag-body">
              <MarkdownRenderer :content="parsedMdContent.detail || parsedMdContent.remaining" />
            </div>
          </div>

        </template>

        <!-- Workflow content without tags — render as-is -->
        <template v-else-if="hasWorkflowMd">
          <div class="summary-md-content">
            <MarkdownRenderer :content="workflowMdContent" />
          </div>
        </template>

        <!-- Fallback when no workflow data -->
        <template v-else>
          <div style="font-size: 12px; color: var(--text-ghost); padding: 16px 0; text-align: center">
            Belum ada data AI Summary untuk {{ isOverview ? 'overview' : 'report' }} ini.
          </div>
        </template>
      </div>

      <!-- Back to Top button -->
      <button
        v-show="showBackToTop"
        class="back-to-top-btn"
        @click="scrollToTop"
        title="Kembali ke atas"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
    </div>

    <!-- Chat AI Panel — Yustian Smart AI (Yustian AI Engine) -->
    <div v-show="activePanel === 'chat'" class="rb-panel">
      <ChatAI />
    </div>

    <!-- Prompt Panel -->
    <div v-show="canAccessPrompt && activePanel === 'prompt'" class="right-content prompt-panel">

      <!-- Prompt context header -->
      <div class="prompt-header-bar">
        <span class="prompt-header-label">📝 Main Prompt</span>
        <span class="prompt-header-context">{{ promptLabel }}</span>
      </div>

      <!-- Loading state -->
      <div v-if="promptLoading" class="prompt-loading">
        Memuat prompt...
      </div>

      <!-- Error state -->
      <div v-else-if="promptLoadError" class="prompt-error">
        {{ promptLoadError }}
      </div>

      <!-- No data -->
      <div v-else-if="!promptRows.length" class="prompt-loading">
        Belum ada prompt untuk {{ isOverview ? 'overview' : 'report' }} ini.
      </div>

      <!-- Prompt editor -->
      <template v-else>
        <div
          v-for="(row, i) in promptRows"
          :key="i"
          class="prompt-card"
        >
          <div class="prompt-card-header">
            <span class="prompt-card-seq">{{ row.Sequence ?? (i + 1) }}</span>
            <span class="prompt-card-label">{{ row.SubSegment || row.Segment }}</span>
          </div>
          <textarea
            class="prompt-textarea"
            v-model="editedPromptValues[i]"
            placeholder="Prompt kosong..."
            spellcheck="false"
          ></textarea>
        </div>

        <!-- Action buttons -->
        <div class="prompt-actions">
          <button
            class="prompt-btn prompt-btn-save"
            :class="{ disabled: !promptDirty || savingPrompt }"
            :disabled="!promptDirty || savingPrompt"
            @click="savePrompt"
          >
            {{ savingPrompt ? 'Menyimpan...' : '💾 Simpan' }}
          </button>
          <button
            class="prompt-btn prompt-btn-implement"
            :class="{ disabled: implementingPrompt }"
            :disabled="implementingPrompt"
            @click="implementPrompt"
          >
            {{ implementingPrompt ? 'Mengimplementasi...' : '🚀 Implementasi' }}
          </button>
        </div>

        <!-- Status messages -->
        <div v-if="promptSaveStatus" class="prompt-status" :class="promptSaveStatus">
          {{ promptSaveMessage }}
        </div>
        <div v-if="promptImplementStatus" class="prompt-status" :class="promptImplementStatus">
          {{ promptImplementMessage }}
        </div>

        <!-- Dirty indicator -->
        <div v-if="promptDirty" class="prompt-dirty-hint">
          Ada perubahan yang belum disimpan
        </div>
      </template>
    </div>

    <!-- Users Management Panel -->
    <div v-show="canAccessUsers && activePanel === 'users'" class="right-content users-panel">

      <!-- Header -->
      <div class="um-header-bar">
        <span class="um-header-label">👥 User Management</span>
        <div class="um-header-actions">
          <button class="um-btn-cli" @click="showCliModal = true" title="SSH Production Server">🖥️ CLI Server</button>
          <button class="um-btn-add" @click="openCreateForm">+ Tambah User</button>
        </div>
      </div>

      <!-- Toolbar: cari user + buka/tutup semua -->
      <div v-if="!umLoading && umUsers.length" class="um-toolbar">
        <div class="um-search">
          <span class="um-search-icon">🔍</span>
          <input
            v-model="umSearch"
            type="text"
            class="um-search-input"
            placeholder="Cari nama / posisi / dept…"
            data-component-id="users--input--search"
          />
          <button v-if="umSearch" class="um-search-clear" @click="umSearch = ''" title="Bersihkan">✕</button>
        </div>
        <div class="um-toolbar-right">
          <span class="um-count">{{ filteredUsers.length }}/{{ umUsers.length }}</span>
          <button class="um-bulk-btn" @click="expandAll" title="Buka semua">⊕</button>
          <button class="um-bulk-btn" @click="collapseAll" title="Tutup semua">⊖</button>
        </div>
      </div>

      <!-- Status messages -->
      <div v-if="umSaveSuccess" class="prompt-status success" style="margin: 0 0 8px">
        {{ umSaveSuccess }}
      </div>
      <div v-if="umSaveError" class="prompt-status error" style="margin: 0 0 8px">
        {{ umSaveError }}
      </div>

      <!-- Loading -->
      <div v-if="umLoading" class="prompt-loading">Memuat data user...</div>

      <!-- Error loading -->
      <div v-else-if="umLoadError" class="prompt-error">{{ umLoadError }}</div>

      <!-- User List (accordion — collapse by default) -->
      <div v-if="!umLoading && filteredUsers.length" class="um-list">
        <div
          v-for="u in filteredUsers"
          :key="u.userId"
          class="um-user-card"
          :class="{ inactive: !u.isActive, 'is-open': isUserExpanded(u.userId) }"
        >
          <!-- Header accordion: selalu tampil, klik untuk buka/tutup -->
          <button
            type="button"
            class="um-acc-head"
            :aria-expanded="isUserExpanded(u.userId) ? 'true' : 'false'"
            :data-component-id="`users--acc-head--${u.userId}`"
            @click="toggleUser(u.userId)"
          >
            <span class="um-acc-chevron" aria-hidden="true">▶</span>
            <span class="um-acc-name">{{ u.userName }}</span>
            <span v-if="u.position" class="um-acc-pos">{{ u.position }}</span>
            <span class="um-acc-spacer"></span>
            <span
              class="um-status-dot"
              :class="u.isActive ? 'on' : 'off'"
              :title="u.isActive ? 'Aktif' : 'Nonaktif'"
            ></span>
            <span class="um-user-id">#{{ u.userId }}</span>
          </button>

          <!-- Body accordion: hanya di-mount saat dibuka -->
          <div v-if="isUserExpanded(u.userId)" class="um-acc-body">
            <div v-if="u.gender" class="um-user-pos">
              <span class="um-gender-pill">{{ u.gender }}</span>
            </div>
            <div v-if="u.jobDesk || u.goal || u.qualification || u.skill" class="um-user-profile">
              <div v-if="u.jobDesk" class="um-profile-block">
                <span class="um-profile-key">📋 Job Desk</span>
                <MarkdownRenderer :content="u.jobDesk" class="um-md" />
              </div>
              <div v-if="u.goal" class="um-profile-block">
                <span class="um-profile-key">🎯 Goal</span>
                <MarkdownRenderer :content="u.goal" class="um-md" />
              </div>
              <div v-if="u.qualification" class="um-profile-block">
                <span class="um-profile-key">🎓 Kualifikasi</span>
                <MarkdownRenderer :content="u.qualification" class="um-md" />
              </div>
              <div v-if="u.skill" class="um-profile-block">
                <span class="um-profile-key">🛠️ Skill</span>
                <MarkdownRenderer :content="u.skill" class="um-md" />
              </div>
            </div>
            <div class="um-user-depts">
              <span
                v-for="d in u.allowedDepts"
                :key="d"
                class="um-dept-badge"
              >{{ getDeptIcon(d) }} {{ getDeptLabel(d) }}</span>
              <span v-if="!u.allowedDepts.length" class="um-no-dept">Tidak ada akses</span>
            </div>
            <div class="um-user-meta">
              <span v-if="u.isPrompt" class="um-badge um-badge-prompt">Prompt</span>
              <span v-if="u.isUsersAdmin" class="um-badge um-badge-admin">Admin</span>
              <span class="um-badge" :class="u.isActive ? 'um-badge-active' : 'um-badge-inactive'">
                {{ u.isActive ? 'Aktif' : 'Nonaktif' }}
              </span>
              <span v-if="u.expiresAt" class="um-expires">Exp: {{ formatDate(u.expiresAt) }}</span>
            </div>
            <div class="um-user-token">
              <span class="um-token-label">Token:</span>
              <code class="um-token-value">{{ u.securityToken }}</code>
              <button class="um-copy-btn" @click="copyToken(u.securityToken)" title="Copy token">📋</button>
            </div>
            <div class="um-user-actions">
              <button class="um-action-btn um-edit" @click="openEditForm(u)">Edit</button>
              <button class="um-action-btn um-delete" @click="handleDeleteUser(u)">Hapus</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state: belum ada user -->
      <div v-else-if="!umLoading && !umLoadError && !umUsers.length" class="prompt-loading">
        Belum ada user terdaftar.
      </div>

      <!-- Empty state: pencarian tidak cocok -->
      <div v-else-if="!umLoading && !umLoadError && umUsers.length && !filteredUsers.length" class="prompt-loading">
        Tidak ada user cocok dengan “{{ umSearch }}”.
      </div>
    </div>

    <!-- Insights Mining Panel (admin-only) -->
    <div v-show="canAccessUsers && activePanel === 'mining'" class="right-content mining-tab-content">
      <InsightsMiningPanel />
    </div>

    <!-- Delete Confirmation Modal -->
    <Teleport to="body">
      <Transition name="confirm-fade">
        <div v-if="confirmDelete.show" class="confirm-overlay" @click.self="cancelDelete">
          <div class="confirm-dialog">
            <div class="confirm-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <h3 class="confirm-title">Hapus User</h3>
            <p class="confirm-message">
              Apakah kamu yakin ingin menghapus<br>
              <strong>{{ confirmDelete.userName }}</strong>?
            </p>
            <p class="confirm-warning">Tindakan ini tidak dapat dibatalkan.</p>
            <div class="confirm-actions">
              <button class="confirm-btn confirm-cancel" @click="cancelDelete">Batal</button>
              <button class="confirm-btn confirm-delete" @click="executeDelete">Hapus</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

  <!-- CLI Server popup modal -->
  <Teleport to="body">
    <Transition name="cli-modal">
      <div v-if="showCliModal" class="cli-modal-overlay" @click.self="showCliModal = false">
        <div class="cli-modal">
          <div class="cli-modal-header">
            <span>🖥️ CLI Server — Production</span>
            <button class="cli-modal-close" @click="showCliModal = false">✕</button>
          </div>
          <div class="cli-modal-body">
            <div class="cli-row">
              <span class="cli-label">Production</span>
              <code class="cli-cmd">ssh -p 8873 root@103.167.113.91</code>
              <button class="cli-copy-btn" @click="cliCopy('ssh -p 8873 root@103.167.113.91', 'prod')" :title="cliCopied === 'prod' ? 'Tersalin!' : 'Salin'">
                {{ cliCopied === 'prod' ? '✓' : '📋' }}
              </button>
            </div>
            <div class="cli-row">
              <span class="cli-label">Source Code</span>
              <code class="cli-cmd">ssh -p 8876 root@192.168.10.212</code>
              <button class="cli-copy-btn" @click="cliCopy('ssh -p 8876 root@192.168.10.212', 'src')" :title="cliCopied === 'src' ? 'Tersalin!' : 'Salin'">
                {{ cliCopied === 'src' ? '✓' : '📋' }}
              </button>
            </div>
            <p class="cli-note">⚠️ Source code server: jangan jalankan program di sini, hanya untuk edit kode.</p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- User Add/Edit Modal -->
  <Teleport to="body">
    <Transition name="um-modal">
      <div v-if="showUserForm" class="um-modal-overlay" @click.self="closeForm">
        <div class="um-modal-card" role="dialog" aria-modal="true">
          <div class="um-modal-header">
            <span>{{ editingUser ? '✏️ Edit User' : '➕ Tambah User Baru' }}</span>
            <button class="um-modal-close" @click="closeForm" title="Tutup (Esc)">✕</button>
          </div>
          <div class="um-modal-body">
            <label class="um-label">Nama User</label>
            <input
              class="um-input"
              v-model="formUserName"
              placeholder="Nama user..."
              spellcheck="false"
              data-component-id="users--input--name"
            />

            <div class="um-profile-grid">
              <div>
                <label class="um-label">Gender</label>
                <select
                  class="um-input"
                  v-model="formGender"
                  data-component-id="users--select--gender"
                >
                  <option value="">— Pilih —</option>
                  <option value="Pria">Pria</option>
                  <option value="Wanita">Wanita</option>
                  <option value="Lainnya">Lainnya</option>
                </select>
              </div>
              <div>
                <label class="um-label">Posisi / Jabatan</label>
                <input
                  class="um-input"
                  v-model="formPosition"
                  placeholder="cth. Marketing Manager"
                  maxlength="150"
                  spellcheck="false"
                  data-component-id="users--input--position"
                />
              </div>
            </div>

            <label class="um-label">Job Desk</label>
            <textarea
              class="um-input um-textarea"
              v-model="formJobDesk"
              placeholder="Tugas utama user di posisi ini — agar AI mengerti konteks pekerjaannya."
              rows="3"
              maxlength="10000"
              spellcheck="false"
              data-component-id="users--textarea--job-desk"
            ></textarea>

            <label class="um-label">Goal / Kepentingan User</label>
            <textarea
              class="um-input um-textarea"
              v-model="formGoal"
              placeholder="Apa yang ingin dicapai user dari laporan/AI ini — agar reply AI tepat sasaran."
              rows="3"
              maxlength="10000"
              spellcheck="false"
              data-component-id="users--textarea--goal"
            ></textarea>

            <label class="um-label">Kualifikasi</label>
            <textarea
              class="um-input um-textarea"
              v-model="formQualification"
              placeholder="Latar belakang pendidikan, sertifikasi, pengalaman — agar AI bisa menyesuaikan kedalaman teknis."
              rows="3"
              maxlength="10000"
              spellcheck="false"
              data-component-id="users--textarea--qualification"
            ></textarea>

            <label class="um-label">Skill yang Dibutuhkan</label>
            <textarea
              class="um-input um-textarea"
              v-model="formSkill"
              placeholder="Keahlian teknis & domain yang relevan dengan posisi user (cth. SQL, BI tools, copywriting, negosiasi)."
              rows="3"
              maxlength="10000"
              spellcheck="false"
              data-component-id="users--textarea--skill"
            ></textarea>

            <label class="um-label">Akses Department</label>
            <div class="um-dept-grid">
              <label
                v-for="dept in umDeptOptions"
                :key="dept.id"
                class="um-dept-check"
                :class="{ checked: formAllowedDepts.includes(dept.id) }"
              >
                <input
                  type="checkbox"
                  :value="dept.id"
                  v-model="formAllowedDepts"
                  class="um-checkbox"
                />
                <span class="um-dept-icon">{{ dept.icon }}</span>
                <span class="um-dept-name">{{ dept.label }}</span>
              </label>
            </div>

            <div class="um-form-row">
              <label class="um-toggle-label">
                <input type="checkbox" v-model="formIsPrompt" class="um-checkbox" />
                <span>Akses Prompt</span>
              </label>
              <label class="um-toggle-label">
                <input type="checkbox" v-model="formIsUsersAdmin" class="um-checkbox" />
                <span>Users Admin</span>
              </label>
              <label class="um-toggle-label">
                <input type="checkbox" v-model="formIsActive" class="um-checkbox" />
                <span>Aktif</span>
              </label>
            </div>

            <label class="um-label">Kadaluarsa (opsional)</label>
            <input
              class="um-input"
              type="date"
              v-model="formExpiresAt"
            />

            <div v-if="umSaveError" class="prompt-status error" style="margin-top: 10px">
              {{ umSaveError }}
            </div>
          </div>

          <div class="um-modal-footer">
            <button class="prompt-btn" @click="closeForm">Batal</button>
            <button
              class="prompt-btn prompt-btn-save"
              :disabled="umSaving || !formUserName.trim()"
              :class="{ disabled: umSaving || !formUserName.trim() }"
              @click="handleSaveUser"
            >
              {{ umSaving ? 'Menyimpan...' : (editingUser ? '💾 Update' : '💾 Simpan') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useActiveReport } from '../composables/useActiveReport'
import { useWorkflowData } from '../composables/useWorkflowData'
import { parseOverviewTags } from '../utils/parseOverviewTags'
import {
  exportAsPdf, exportAsDocx, copyToClipboard,
  buildShareText, stripMarkdown,
} from '../utils/exportUtils'
import MarkdownRenderer from './MarkdownRenderer.vue'
import ChatAI from './ChatAI.vue'
import InsightsMiningPanel from './InsightsMiningPanel.vue'
import { usePromptData } from '../composables/usePromptData'
import { useUserManagement } from '../composables/useUserManagement'
import { useAuth } from '../composables/useAuth'

const { currentDept, currentReport, isOverview, reportContextKey } = useActiveReport()
const { getReportContent, overviewContent, isLoading: wfLoading } = useWorkflowData()
const { canAccessPrompt, canAccessUsers } = useAuth()

const {
  promptData: promptRows,
  editedPrompts: editedPromptValues,
  isLoading: promptLoading,
  isSaving: savingPrompt,
  isImplementing: implementingPrompt,
  loadError: promptLoadError,
  saveStatus: promptSaveStatus,
  saveMessage: promptSaveMessage,
  implementStatus: promptImplementStatus,
  implementMessage: promptImplementMessage,
  isDirty: promptDirty,
  currentPromptLabel: promptLabel,
  savePrompt,
  implementPrompt,
} = usePromptData()

// ─── User Management ───
const {
  users: umUsers,
  isLoading: umLoading,
  isSaving: umSaving,
  loadError: umLoadError,
  saveError: umSaveError,
  saveSuccess: umSaveSuccess,
  deptOptions: umDeptOptions,
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  clearMessages: umClearMessages,
} = useUserManagement()

// ─── User card accordion + search (collapse by default) ───
const umSearch = ref('')
const filteredUsers = computed(() => {
  const q = umSearch.value.trim().toLowerCase()
  if (!q) return umUsers.value
  return umUsers.value.filter((u) => {
    const hay = [
      u.userName, u.position, u.jobDesk, u.goal, u.qualification, u.skill,
      ...(u.allowedDepts || []).map((d) => getDeptLabel(d)),
    ].filter(Boolean).join(' ').toLowerCase()
    return hay.includes(q)
  })
})

const expandedUsers = ref(new Set())
function isUserExpanded(userId) {
  return expandedUsers.value.has(userId)
}
function toggleUser(userId) {
  const next = new Set(expandedUsers.value)
  next.has(userId) ? next.delete(userId) : next.add(userId)
  expandedUsers.value = next
}
function expandAll() {
  expandedUsers.value = new Set(filteredUsers.value.map((u) => u.userId))
}
function collapseAll() {
  expandedUsers.value = new Set()
}

// ─── CLI Server modal ───
const showCliModal = ref(false)
const cliCopied = ref(null)

function cliCopy(text, key) {
  navigator.clipboard.writeText(text).then(() => {
    cliCopied.value = key
    setTimeout(() => { cliCopied.value = null }, 2000)
  })
}

const showUserForm = ref(false)
const editingUser = ref(null) // null = create mode, object = edit mode
const formUserName = ref('')
const formAllowedDepts = ref([])
const formIsPrompt = ref(false)
const formIsUsersAdmin = ref(false)
const formIsActive = ref(true)
const formExpiresAt = ref('')
const formGender = ref('')
const formPosition = ref('')
const formJobDesk = ref('')
const formGoal = ref('')
const formQualification = ref('')
const formSkill = ref('')

function openCreateForm() {
  umClearMessages()
  editingUser.value = null
  formUserName.value = ''
  formAllowedDepts.value = []
  formIsPrompt.value = false
  formIsUsersAdmin.value = false
  formIsActive.value = true
  formExpiresAt.value = ''
  formGender.value = ''
  formPosition.value = ''
  formJobDesk.value = ''
  formGoal.value = ''
  formQualification.value = ''
  formSkill.value = ''
  showUserForm.value = true
}

function openEditForm(user) {
  umClearMessages()
  editingUser.value = user
  formUserName.value = user.userName
  formAllowedDepts.value = [...user.allowedDepts]
  formIsPrompt.value = user.isPrompt
  formIsUsersAdmin.value = user.isUsersAdmin
  formIsActive.value = user.isActive
  formExpiresAt.value = user.expiresAt ? user.expiresAt.split('T')[0] : ''
  formGender.value = user.gender || ''
  formPosition.value = user.position || ''
  formJobDesk.value = user.jobDesk || ''
  formGoal.value = user.goal || ''
  formQualification.value = user.qualification || ''
  formSkill.value = user.skill || ''
  showUserForm.value = true
}

function closeForm() {
  showUserForm.value = false
  editingUser.value = null
}

function handleUserModalEsc(e) {
  if (e.key === 'Escape' && showUserForm.value) closeForm()
}

watch(showUserForm, (open) => {
  if (open) {
    document.addEventListener('keydown', handleUserModalEsc)
    document.body.style.overflow = 'hidden'
  } else {
    document.removeEventListener('keydown', handleUserModalEsc)
    document.body.style.overflow = ''
  }
})

async function handleSaveUser() {
  const payload = {
    userName: formUserName.value.trim(),
    allowedDepts: formAllowedDepts.value,
    isPrompt: formIsPrompt.value,
    isUsersAdmin: formIsUsersAdmin.value,
    isActive: formIsActive.value,
    expiresAt: formExpiresAt.value || null,
    gender:        formGender.value.trim(),
    position:      formPosition.value.trim(),
    jobDesk:       formJobDesk.value.trim(),
    goal:          formGoal.value.trim(),
    qualification: formQualification.value.trim(),
    skill:         formSkill.value.trim(),
  }
  if (editingUser.value) {
    const ok = await updateUser(editingUser.value.userId, payload)
    if (ok) {
      closeForm()
      fetchUsers()
    }
  } else {
    const created = await createUser(payload)
    if (created) {
      closeForm()
    }
  }
}

const confirmDelete = ref({ show: false, userId: null, userName: '' })

function handleDeleteUser(user) {
  confirmDelete.value = { show: true, userId: user.userId, userName: user.userName }
}

function cancelDelete() {
  confirmDelete.value = { show: false, userId: null, userName: '' }
}

async function executeDelete() {
  const { userId } = confirmDelete.value
  confirmDelete.value = { show: false, userId: null, userName: '' }
  if (userId) await deleteUser(userId)
}

function getDeptIcon(deptId) {
  const found = umDeptOptions.find(d => d.id === deptId)
  return found ? found.icon : '📁'
}

function getDeptLabel(deptId) {
  const found = umDeptOptions.find(d => d.id === deptId)
  return found ? found.label : deptId
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
}

function copyToken(token) {
  const url = `${window.location.origin}/${token}`
  navigator.clipboard.writeText(url).catch(() => {})
}

// Default tab = 'chat' (surface chat Google Ads paling kapabel tampil duluan),
// atau tab terakhir yang dipilih user. Hanya summary/chat yang dipersist —
// tab admin (prompt/users/mining) sengaja tidak, agar tidak mendarat di tab
// yang mungkin tak lagi diizinkan.
const PANEL_STORE_KEY = 'urd.rightPanel'
const activePanel = ref((() => {
  try {
    const v = localStorage.getItem(PANEL_STORE_KEY)
    if (v === 'summary' || v === 'chat') return v
  } catch { /* ignore */ }
  return 'chat'
})())
watch(canAccessPrompt, (val) => {
  if (!val && activePanel.value === 'prompt') activePanel.value = 'summary'
})
watch(canAccessUsers, (val) => {
  if (!val && (activePanel.value === 'users' || activePanel.value === 'mining')) {
    activePanel.value = 'summary'
  }
})

// Load users when switching to users tab + persist tab terakhir (summary/chat).
watch(activePanel, (panel) => {
  if (panel === 'users') {
    fetchUsers()
  }
  if (panel === 'summary' || panel === 'chat') {
    try { localStorage.setItem(PANEL_STORE_KEY, panel) } catch { /* ignore */ }
  }
})
const showSummaryExport = ref(false)
const summaryBoxEl = ref(null)
const summaryScrollEl = ref(null)
const showBackToTop = ref(false)

function onSummaryScroll() {
  if (!summaryScrollEl.value) return
  showBackToTop.value = summaryScrollEl.value.scrollTop > 200
}

function scrollToTop() {
  if (!summaryScrollEl.value) return
  summaryScrollEl.value.scrollTo({ top: 0, behavior: 'smooth' })
}

// Workflow MDFile content for active report
const workflowMdContent = computed(() => {
  if (isOverview.value) {
    return overviewContent.value || null
  }
  if (!currentReport.value) return null
  const wf = getReportContent(currentReport.value.id)
  return wf?.mdFile || null
})

const hasWorkflowMd = computed(() => !!workflowMdContent.value)

// Parse tags from workflow content
const parsedMdContent = computed(() => parseOverviewTags(workflowMdContent.value))

// Check if any recognized section exists
const hasParsedSections = computed(() => {
  const p = parsedMdContent.value
  return !!(p.executiveSummary || p.todoList || p.todoPoints.length || p.detail || p.detailPoints.length)
})

// Helpers: extract title (first line) and body (rest) from a point
function getPointTitle(point) {
  const first = point.split('\n')[0].replace(/^\*\*(.+)\*\*$/, '$1').replace(/^#+\s*/, '')
  return first || 'Item'
}
function getPointBody(point) {
  const lines = point.split('\n')
  return lines.slice(1).join('\n').trim()
}

const summaryExportLabel = computed(() =>
  isOverview.value
    ? `${currentDept.value.label} Overview — AI Summary`
    : `${currentReport.value.label} — AI Summary`
)

function handleSummaryExport(type) {
  showSummaryExport.value = false

  const label = summaryExportLabel.value

  function getSummaryText() {
    let summary = ''
    if (hasWorkflowMd.value) {
      summary = stripMarkdown(workflowMdContent.value)
    }
    return buildShareText({
      title: label,
      summary,
    })
  }

  switch (type) {
    case 'pdf':
      exportAsPdf(summaryBoxEl.value, label)
      break
    case 'docx':
      exportAsDocx(summaryBoxEl.value, label)
      break
    case 'clipboard':
      copyToClipboard(getSummaryText())
      break
  }
}

watch(reportContextKey, () => {
  showSummaryExport.value = false
})

// ─── Panel Resize ───
const PANEL_LEFT_WIDTH = 180
const PANEL_RIGHT_MIN_WIDTH = 230
const PANEL_RIGHT_MAX_WIDTH = 1400

// Default: split available space (after PanelLeft) 50/50 between PanelCenter and PanelRight
function computeDefaultWidth() {
  if (typeof window === 'undefined') return PANEL_RIGHT_MIN_WIDTH
  const available = window.innerWidth - PANEL_LEFT_WIDTH
  const half = Math.round(available / 2)
  return Math.max(PANEL_RIGHT_MIN_WIDTH, Math.min(PANEL_RIGHT_MAX_WIDTH, half))
}

const panelWidth = ref(computeDefaultWidth())
const userResized = ref(false)
const isResizing = ref(false)
let resizeState = null

function startResize(e) {
  resizeState = { startX: e.clientX, startWidth: panelWidth.value }
  isResizing.value = true
  userResized.value = true
  document.addEventListener('mousemove', doResize)
  document.addEventListener('mouseup', stopResize)
  document.body.style.userSelect = 'none'
  document.body.style.cursor = 'col-resize'
}

function doResize(e) {
  if (!resizeState) return
  const delta = resizeState.startX - e.clientX
  panelWidth.value = Math.max(PANEL_RIGHT_MIN_WIDTH, Math.min(PANEL_RIGHT_MAX_WIDTH, resizeState.startWidth + delta))
}

function stopResize() {
  resizeState = null
  isResizing.value = false
  document.removeEventListener('mousemove', doResize)
  document.removeEventListener('mouseup', stopResize)
  document.body.style.userSelect = ''
  document.body.style.cursor = ''
}

// Re-apply 50/50 split on window resize until the user manually resizes the panel
function handleWindowResize() {
  if (userResized.value) return
  panelWidth.value = computeDefaultWidth()
}

onMounted(() => {
  panelWidth.value = computeDefaultWidth()
  window.addEventListener('resize', handleWindowResize)
})

onUnmounted(() => {
  document.removeEventListener('mousemove', doResize)
  document.removeEventListener('mouseup', stopResize)
  window.removeEventListener('resize', handleWindowResize)
  document.removeEventListener('keydown', handleUserModalEsc)
  document.body.style.overflow = ''
})
</script>
